import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { HeaderComponent } from "../components/header/header.component";
import { SidebarComponent } from "../components/sidebar/sidebar.component";
import { SubheaderComponent } from "../components/subheader/subheader.component";
import { DropdownModule } from "primeng/dropdown";
import { InputTextModule } from "primeng/inputtext";
import { CalendarModule } from "primeng/calendar";
import { CustomValidators } from "../shared/validators/custom-validators";
import { GeneralModalComponent } from "../shared/components/general-modal/general-modal.component";
import { CURP_HELP_IMG } from "../assets/constants";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

@Component({
  selector: "app-personal-data",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    SidebarComponent,
    SubheaderComponent,
    DropdownModule,
    InputTextModule,
    CalendarModule,
    GeneralModalComponent,
  ],
  templateUrl: "./personal-data.component.html",
  styleUrl: "./personal-data.component.css",
})
export class PersonalDataComponent implements OnInit {
  personalDataForm!: FormGroup;
  showCurpHelpModal: boolean = false;
  curpHelpImgSafe: SafeHtml = "";
  // Posición del recuadro (porcentaje sobre la imagen). Ajusta si tu imagen varía.
  highlightStyle = {
    top: "63%", // distancia desde arriba
    left: "8%", // desde la izquierda
    width: "84%", // ancho del recuadro
    height: "12%", // alto del recuadro
  };

  genderOptions = [
    { label: "Masculino", value: "M" },
    { label: "Femenino", value: "F" },
  ];

  countryOptions = [
    { label: "México", value: "MX" },
    { label: "Estados Unidos", value: "US" },
    { label: "Canadá", value: "CA" },
  ];

  stateOptions = [
    { label: "Ciudad de México", value: "CDMX" },
    { label: "Estado de México", value: "MEX" },
    { label: "Jalisco", value: "JAL" },
    { label: "Nuevo León", value: "NL" },
  ];

  maritalStatusOptions = [
    { label: "Soltero(a)", value: "single" },
    { label: "Casado(a)", value: "married" },
    { label: "Divorciado(a)", value: "divorced" },
    { label: "Viudo(a)", value: "widowed" },
  ];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer
  ) {
    this.curpHelpImgSafe =
      this.sanitizer.bypassSecurityTrustHtml(CURP_HELP_IMG);
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.personalDataForm = this.fb.group({
      curp: ["", [Validators.required, CustomValidators.curp]],
      firstName: ["", [Validators.required, Validators.minLength(2)]],
      paternalLastName: ["", [Validators.required, Validators.minLength(2)]],
      maternalLastName: ["", [Validators.required, Validators.minLength(2)]],
      rfc: ["", [Validators.required, CustomValidators.rfc]],
      homoclave: ["", [Validators.required, CustomValidators.homoclave]],
      birthDate: ["", [Validators.required, CustomValidators.birthDate]],
      gender: ["", Validators.required],
      birthCountry: ["", Validators.required],
      birthState: ["", Validators.required],
      maritalStatus: ["", Validators.required],
      //nationality: ['', Validators.required]
    });
  }

  // Getters para fácil acceso a los controles
  get curp() {
    return this.personalDataForm.get("curp");
  }
  get firstName() {
    return this.personalDataForm.get("firstName");
  }
  get paternalLastName() {
    return this.personalDataForm.get("paternalLastName");
  }
  get maternalLastName() {
    return this.personalDataForm.get("maternalLastName");
  }
  get rfc() {
    return this.personalDataForm.get("rfc");
  }
  get homoclave() {
    return this.personalDataForm.get("homoclave");
  }
  get birthDate() {
    return this.personalDataForm.get("birthDate");
  }
  get gender() {
    return this.personalDataForm.get("gender");
  }
  get birthCountry() {
    return this.personalDataForm.get("birthCountry");
  }
  get birthState() {
    return this.personalDataForm.get("birthState");
  }
  get maritalStatus() {
    return this.personalDataForm.get("maritalStatus");
  }
  //get nationality() { return this.personalDataForm.get('nationality'); }

  // Método para obtener mensaje de error
  getErrorMessage(controlName: string): string {
    const control = this.personalDataForm.get(controlName);
    if (!control || !control.errors || !control.touched) return "";

    const errors = control.errors;

    if (errors["required"]) return "Este campo es obligatorio";
    if (errors["invalidCurp"]) return "CURP inválido";
    if (errors["invalidRfc"]) return "RFC inválido";
    if (errors["invalidHomoclave"]) return "Homoclave inválida";
    if (errors["invalidDateFormat"])
      return "Formato de fecha inválido (DD/MM/AAAA)";
    if (errors["invalidDate"]) return "Fecha inválida";
    if (errors["underAge"]) return "Debes ser mayor de 18 años";
    if (errors["futureDate"]) return "La fecha no puede ser futura";
    if (errors["minlength"])
      return `Mínimo ${errors["minlength"].requiredLength} caracteres`;

    return "Campo inválido";
  }

  // Método para verificar si un campo tiene errores
  hasError(controlName: string): boolean {
    const control = this.personalDataForm.get(controlName);
    return !!(control && control.errors && control.touched);
  }

  // Formatear entrada de fecha
  formatDateInput(event: any): void {
    let value = event.target.value.replace(/\D/g, "");

    if (value.length >= 2) {
      value = value.substring(0, 2) + "/" + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + "/" + value.substring(5, 9);
    }

    event.target.value = value;
    this.personalDataForm.get("birthDate")?.setValue(value);
  }

  // Solo permitir números
  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return charCode >= 48 && charCode <= 57;
  }

  goBack(): void {
    this.router.navigate(["/otp"]);
  }

  onSubmit(): void {
    if (this.personalDataForm.valid) {
      console.log("Datos personales válidos:", this.personalDataForm.value);
      this.router.navigate(["/address-personal"]);
    } else {
      this.personalDataForm.markAllAsTouched();
      console.log("Formulario inválido");
    }
  }

  closeCurpHelp() {
    this.showCurpHelpModal = false;
  }
}
